import React from 'react';
import styled from 'styled-components';

import {getApiUrl} from '../lib/api';
import Nl2br from './Nl2br';
import {buildCatalogMap} from '../lib/catalog';
import {getMeasurementUnitLabel, getUnitLabel, isInventoryQuestion, getInventoryItemSource} from '../screens/FormBuilderScreen/shared';

// The width an image or signature answer is shown at.
export const IMAGE_ANSWER_WIDTH = 200;

function Answer({question, answers, catalog, onImageLoaded = () => {}}) {
  if(!answers || answers.length < 1) {
    return <span>(No answer)</span>;
  }

  switch(question.type) {
    case 'SIGNATURE':
    case 'IMAGE': {
      const extension = question.type === 'SIGNATURE' ? 'png' : 'jpg';
      return (
        <a href={`${getApiUrl()}/image/${answers[0].value}.${extension}`} target="_blank" rel="noopener noreferrer">
          <Image
            src={`${getApiUrl()}/image/${answers[0].value}`}
            alt={`${question.text} answer`}
            width={IMAGE_ANSWER_WIDTH}
            onLoad={onImageLoaded}
          />
        </a>
      );
    }
    case 'CODE_GENERATOR':
    case 'SCANNED_CODE':
    case 'TEXT':
      return <Nl2br>{answers[0].value}</Nl2br>;
    default:
      return <span>{getAnswerText(question, answers, needsCatalogMap(question) ? buildCatalogMap(catalog) : null)}</span>;
  }
}

/**
 * The text an answer is shown as, for everything but images and signatures, which are shown as a picture and have
 * none. Catalog answers are named from catalogMap, built with buildCatalogMap.
 */
export function getAnswerText(question, answers, catalogMap) {
  if(!answers || answers.length < 1) {
    return '(No answer)';
  }

  switch(question.type) {
    case 'SIGNATURE':
    case 'IMAGE':
      return null;
    case 'BOOLEAN':
      return (answers[0].value === "true" || answers[0].value === true) ? 'Yes (True)' : 'No (False)';
    case 'DATETIME':
      return new Date(parseInt(answers[0].value.split(',')[0], 10)).toLocaleString();
    case 'CODE_GENERATOR':
    case 'SCANNED_CODE':
    case 'TEXT':
      return answers[0].value;
    case 'CATALOG_CHECKBOX':
    case 'CATALOG_RADIO':
      return answers.map(answer => answer.value.split(',').map(id => catalogMap[id.trim()] ? catalogMap[id.trim()].name : '(deleted)').join(' → ')).join(', ');
    case 'MEASURED_NUMERIC': {
      const unit = getMeasurementUnitLabel(question);
      const sorted = answers.sort((a, b) => a.createdDate - b.createdDate);
      return sorted.map(answer => unit ? `${answer.value} ${unit}` : answer.value).join(', ');
    }
    case 'INVENTORY_RECEIVING':
    case 'INVENTORY_USAGE':
      return answers.sort((a, b) => a.createdDate - b.createdDate)
        .map(answer => getInventoryAnswerText(question, answer, catalogMap))
        .join(', ');
    default:
      return answers.sort((a, b) => a.createdDate - b.createdDate).map(answer => answer.value).join(', ');
  }
}

// The value of an inventory answer is the amount. The item and lot come beside it in answer.inventory, which can be
// missing (for example an answer sent without them), so the amount is always shown.
function getInventoryAnswerText(question, answer, catalogMap) {
  const inventory = answer.inventory || null;
  const unit = (inventory && getUnitLabel(inventory.unit)) || getMeasurementUnitLabel(question);
  const amount = `Amount: ${unit ? `${answer.value} ${unit}` : answer.value}`;

  if (!inventory) {
    return amount;
  }

  const item = getInventoryItemName(inventory, catalogMap);

  return [
    item ? `Item: ${item}` : null,
    inventory.lot ? `Lot: ${inventory.lot}` : null,
    amount
  ].filter(Boolean).join(', ');
}

function getInventoryItemName(inventory, catalogMap) {
  if (inventory.itemSource !== 'CATALOG') {
    return inventory.item || null;
  }

  // The leaf id is parsed by the api. It is null when what was sent was not a catalog id.
  if (!inventory.catalogId) {
    return '(unknown item)';
  }

  return catalogMap && catalogMap[inventory.catalogId] ? catalogMap[inventory.catalogId].name : '(deleted)';
}

export function isCatalogQuestion(question) {
  return question.type === 'CATALOG_CHECKBOX' || question.type === 'CATALOG_RADIO';
}

// True when getAnswerText needs a catalogMap to name the answer.
export function needsCatalogMap(question) {
  return isCatalogQuestion(question) || (isInventoryQuestion(question) && getInventoryItemSource(question) === 'CATALOG');
}

const Image = styled.img`
  border-radius: 0.25rem;
  display: block;
`;

export default Answer;
